import { useMemo } from 'react';
import getConfig from 'next/config';
import { RawIntlProvider } from 'react-intl';
import { createIntl, createIntlCache } from 'react-intl/server';

import { LanguagesLong } from '~/helpers/LanguageContext/LanguageContext.helpers';

/** @param {LanguagesLong} value */
function isLanguagesLong(value) {
  return LanguagesLong.includes(value);
}

/** @param {LanguagesLong} value */
function toLanguagesLong(value) {
  return isLanguagesLong(value) ? value : 'de-DE';
}

const intlCache = createIntlCache();

function withLocale(MyComponent) {
  function ComponentWithLocale(props) {
    const { publicRuntimeConfig } = getConfig();

    const locale = toLanguagesLong(
      props?.locale ?? publicRuntimeConfig.defaultLanguage,
    );

    const messages = props?.messages ?? {}; // eslint-disable-line react-hooks/exhaustive-deps

    const intl = useMemo(() => {
      const baseIntl = createIntl(
        {
          locale,
          defaultLocale: publicRuntimeConfig.defaultLanguage,
          messages,
          defaultRichTextElements: {
            p: (chunks) => <p>{chunks}</p>,
            b: (chunks) => <b>{chunks}</b>,
            h4: (chunks) => <h4>{chunks}</h4>,
          },
        },
        intlCache,
      );

      const origFormatMessage = baseIntl.formatMessage.bind(baseIntl);

      baseIntl.formatMessage = (descriptor, values, opts) => {
        if (!descriptor || !descriptor.id) {
          console.error('[i18n] formatMessage called without id:', descriptor);

          console.error(new Error('[i18n] Missing formatMessage id').stack);
        }
        return origFormatMessage(descriptor, values, opts);
      };

      return baseIntl;
    }, [locale, publicRuntimeConfig.defaultLanguage, messages]);

    return (
      <RawIntlProvider value={intl}>
        <MyComponent {...props} locale={locale} messages={messages} />
      </RawIntlProvider>
    );
  }

  ComponentWithLocale.getInitialProps = async (appContext) => {
    let appProps;
    if (typeof MyComponent.getInitialProps === 'function') {
      appProps = await MyComponent.getInitialProps(appContext);
    }

    const { ctx } = appContext;

    const source = ctx.req || window.__NEXT_DATA__.props;

    const { messages } = source;
    const locale = source.context?.locale;

    return {
      ...appProps,
      locale,
      messages,
    };
  };

  return ComponentWithLocale;
}

export default withLocale;
